import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Home, DollarSign, Bed, Bath } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BackButton from '@/components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800';
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If it's a relative path, prepend the backend URL
  return `${BACKEND_URL}${imagePath}`;
};

const Units = () => {
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [featuredFilter, setFeaturedFilter] = useState(false);
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('');
  const [budgetRange, setBudgetRange] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [units, searchQuery, statusFilter, featuredFilter, propertyTypeFilter, budgetRange]);

  const fetchUnits = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/units`);
      const data = await response.json();
      setUnits(data);
      setFilteredUnits(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching units:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...units];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(unit =>
        unit.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        unit.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (unit.building_name && unit.building_name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(unit => unit.status === statusFilter);
    }

    // Featured filter
    if (featuredFilter) {
      filtered = filtered.filter(unit => unit.featured === true);
    }

    // Property type filter
    if (propertyTypeFilter) {
      filtered = filtered.filter(unit => unit.property_type === propertyTypeFilter);
    }

    // Budget range filter (only for leasing)
    if (statusFilter === 'For Leasing' && budgetRange !== 'all') {
      filtered = filtered.filter(unit => {
        const price = unit.price;
        switch (budgetRange) {
          case '0-1000':
            return price >= 0 && price <= 1000;
          case '1000-2000':
            return price > 1000 && price <= 2000;
          case '2000-3000':
            return price > 2000 && price <= 3000;
          case '3000+':
            return price > 3000;
          default:
            return true;
        }
      });
    }

    setFilteredUnits(filtered);
  };

  const getStatusLabel = (unit) => {
    if (unit.status === 'Sold Out') return 'Sold Out';
    if (unit.status === 'For Sale') return 'Condos Available';
    if (unit.status === 'For Leasing') return 'Leasing Available';
    return unit.status;
  };

  const getStatusColor = (unit) => {
    if (unit.status === 'Sold Out') return 'bg-gray-600';
    if (unit.status === 'For Sale') return 'bg-green-600';
    if (unit.status === 'For Leasing') return 'bg-blue-600';
    return 'bg-gray-600';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section with Background Image */}
      <section 
        className="relative text-white py-24 pt-40"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1594873604892-b599f847e859?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/65 to-black/75"></div>
        
        {/* Back Button */}
        <div className="absolute top-24 left-6 z-10">
          <BackButton />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
              Find Your Perfect Home with GN
            </h1>
            <p className="text-xl text-gray-100">
              Discover exceptional properties for sale and lease in prime locations
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gray-50 rounded-2xl shadow-lg p-8 mb-8">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, location, or building..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA] transition-all"
                />
              </div>
            </div>

            {/* Status and Featured Toggle Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              {['All', 'For Sale', 'For Leasing'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
                    statusFilter === status
                      ? 'bg-[#20B2AA] text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status}
                </button>
              ))}
              <button
                onClick={() => setFeaturedFilter(!featuredFilter)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 ${
                  featuredFilter
                    ? 'bg-yellow-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ⭐ Featured
              </button>
            </div>

            {/* Filter Dropdowns */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Property Type Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Property Type</label>
                <select
                  value={propertyTypeFilter}
                  onChange={(e) => setPropertyTypeFilter(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA] transition-all"
                >
                  <option value="">All Types</option>
                  <option value="Studio">Studio</option>
                  <option value="1 Bed/Bath">1 Bed/Bath</option>
                  <option value="2Bed/Bath">2Bed/Bath</option>
                  <option value="3Bed/Bath">3Bed/Bath</option>
                  <option value="4Bed/Bath">4Bed/Bath</option>
                  <option value=">4 Bed/Bath">&gt;4 Bed/Bath</option>
                </select>
              </div>

              {/* Budget Range Filter - Only show for Leasing */}
              {statusFilter === 'For Leasing' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Monthly Budget
                  </label>
                  <select
                    value={budgetRange}
                    onChange={(e) => setBudgetRange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA] transition-all"
                  >
                    <option value="all">All Budgets</option>
                    <option value="0-1000">$0 - $1,000</option>
                    <option value="1000-2000">$1,000 - $2,000</option>
                    <option value="2000-3000">$2,000 - $3,000</option>
                    <option value="3000+">$3,000+</option>
                  </select>
                </div>
              )}
            </div>

            {/* Results Count */}
            <div className="mt-6 text-center text-gray-600">
              Showing <span className="font-bold text-[#20B2AA]">{filteredUnits.length}</span> properties
            </div>
          </div>

          {/* All Units Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#20B2AA] border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading properties...</p>
            </div>
          ) : filteredUnits.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredUnits.map((unit) => (
                <Link
                  key={unit.id}
                  to={`/units/${unit.slug || unit.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer block"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={getImageUrl(unit.images?.[0])}
                      alt={unit.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    
                    {/* Status Badge */}
                    <div className={`absolute top-4 left-4 ${getStatusColor(unit)} text-white px-4 py-2 rounded-full text-sm font-semibold z-10`}>
                      {getStatusLabel(unit)}
                    </div>
                    
                    {/* Featured Badge */}
                    {unit.featured && (
                      <div className="absolute top-4 right-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold z-10">
                        FEATURED
                      </div>
                    )}

                    {/* Hover Overlay with Action Buttons - Slides up from bottom */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-3 pb-6 px-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `/units/${unit.id}?action=booking`;
                          }}
                          className="bg-white text-[#20B2AA] px-5 py-2 rounded-lg font-medium text-sm hover:bg-[#20B2AA] hover:text-white transition-all duration-200 transform hover:scale-105 shadow-lg"
                        >
                          Book a Viewing
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `/units/${unit.id}?action=enquire`;
                          }}
                          className="bg-[#20B2AA] text-white px-5 py-2 rounded-lg font-medium text-sm hover:bg-white hover:text-[#20B2AA] transition-all duration-200 transform hover:scale-105 border-2 border-white shadow-lg"
                        >
                          Enquire Now
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{unit.name}</h3>
                    <div className="flex items-center text-gray-600 mb-3">
                      <MapPin className="h-4 w-4 mr-2" />
                      <span className="text-sm">{unit.location}</span>
                    </div>

                    <div className="flex items-center gap-4 mb-4 text-gray-700">
                      <div className="flex items-center gap-1">
                        <Bed className="h-4 w-4" />
                        <span className="text-sm">{unit.bedrooms} Bed</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Bath className="h-4 w-4" />
                        <span className="text-sm">{unit.bathrooms} Bath</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Home className="h-4 w-4" />
                        <span className="text-sm">{unit.area}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Price</p>
                        <p className="text-2xl font-bold text-[#20B2AA]">{formatPrice(unit.price)}</p>
                      </div>
                      <div className="text-[#20B2AA] font-semibold text-sm group-hover:translate-x-2 transition-transform duration-200">
                        View Details →
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Home className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No properties found</h3>
              <p className="text-gray-600">Try adjusting your filters to see more results</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Units;
